import { DateTime } from 'luxon';

import { freightBookingWizardMessages } from './freight-booking-wizard-messages';
import { validationMessages } from '../../address/validation-messages';
import { getMissingRequiredHazmatFieldsForCommodity } from '../../ltl/hazmat-validation';

import type { FreightBookingFormState, WizardStepId } from './freight-booking-wizard-types';
import type { LtlAddress, LtlCommodity, LtlHandlingUnit } from '../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

export type WizardFieldError = {
  path: string;
  message: MessageDescriptor;
  values?: Record<string, string | number>;
  /**
   * True when the field holds a value that is present but not allowed, as opposed to
   * merely missing. Consumers surface these immediately — an unusable value can arrive
   * from a hydrated saved quote the user never typed, so waiting for them to visit the
   * step would leave the form silently unquotable.
   */
  isInvalidValue?: boolean;
};

/**
 * Required address fields for the Ship To step. Exported so ShipToStep can consume
 * the same list for its `requiredFields` asterisks, keeping both in sync from a
 * single source of truth.
 *
 * The `satisfies` clause ensures every entry is a valid `LtlAddress` key — a rename
 * of `LtlAddress` that breaks a match here becomes a compile error rather than a
 * silently dead check. Note: AddressForm field ids happen to match these LtlAddress
 * keys exactly; if that ever diverges a separate id-alias layer will be needed.
 */
export const REQUIRED_SHIP_TO_ADDRESS_FIELDS = [
  'name',
  'line1',
  'city',
  'state',
  'postalCode',
  'phone',
  'email',
] as const satisfies readonly (keyof LtlAddress)[];

/** FPS rejects quotes with a pickup date more than this many days after today. */
export const MAX_SHIP_DATE_DAYS_AHEAD = 60;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const { required } = validationMessages;

/** Returns a single-element error if the string value is falsy, otherwise empty. */
const requireText = (path: string, value: string | undefined | null): WizardFieldError[] => (value ? [] : [{ path, message: required }]);

/** Returns a single-element error if the number is not strictly > 0, otherwise empty. */
const requirePositive = (path: string, value: number): WizardFieldError[] => (value > 0 ? [] : [{ path, message: required }]);

/**
 * True when `dateStr` represents a date before today (calendar-day granular, timezone-agnostic
 * string comparison on `yyyy-MM-dd`). Returns false for missing/empty values — callers that need
 * to require a value use `requireShipDate` instead.
 */
export const isShipDateInPast = (dateStr: string | undefined | null): boolean => {
  const dayStr = dateStr?.split('T')[0] ?? '';
  return Boolean(dayStr) && dayStr < DateTime.now().toFormat('yyyy-MM-dd');
};

/** Validates the ship date: required, must not be in the past, and must not exceed the carrier's day cap. */
const requireShipDate = (value: string | undefined | null): WizardFieldError[] => {
  if (!value) return [{ path: 'shipmentDate', message: required }];
  if (isShipDateInPast(value)) {
    return [{ path: 'shipmentDate', message: freightBookingWizardMessages.shipDateInPastError, isInvalidValue: true }];
  }
  const parsed = DateTime.fromISO(value).startOf('day');
  if (parsed.isValid && parsed.diff(DateTime.now().startOf('day'), 'days').days > MAX_SHIP_DATE_DAYS_AHEAD) {
    return [
      {
        path: 'shipmentDate',
        message: freightBookingWizardMessages.shipDateTooFarAhead,
        values: { days: MAX_SHIP_DATE_DAYS_AHEAD },
        isInvalidValue: true,
      },
    ];
  }
  return [];
};

// ---------------------------------------------------------------------------
// Per-step rule functions
// ---------------------------------------------------------------------------

const getShipFromErrors = (state: FreightBookingFormState): WizardFieldError[] => [
  ...requireText('selectedShipFromId', state.selectedShipFromId),
  ...requireShipDate(state.shipmentDate),
  ...(state.willArrangePickupSeparately
    ? []
    : [...requireText('pickupReadyTime', state.pickupReadyTime), ...requireText('pickupCloseTime', state.pickupCloseTime)]),
];

/**
 * Destination fields the rating request actually needs. Deliberately narrower than
 * REQUIRED_SHIP_TO_ADDRESS_FIELDS: mapAddressToBackend drops `email` entirely, and
 * rating only needs the destination geography, so gating rates on the full ship-to
 * set would leave the rates panel empty while contact details are still being typed.
 */
const QUOTE_REQUIRED_ADDRESS_FIELDS = ['city', 'state', 'postalCode'] as const satisfies readonly (keyof LtlAddress)[];

const getAddressErrors = (state: FreightBookingFormState, fields: readonly (keyof LtlAddress)[]): WizardFieldError[] =>
  fields.flatMap((field) => requireText(`destinationAddress.${field}`, state.destinationAddress[field] as string | null | undefined));

const getShipToErrors = (state: FreightBookingFormState): WizardFieldError[] => getAddressErrors(state, REQUIRED_SHIP_TO_ADDRESS_FIELDS);

const getCommodityHazmatErrors = (commodity: LtlCommodity, prefix: string): WizardFieldError[] =>
  [...getMissingRequiredHazmatFieldsForCommodity(commodity)].map((field) => ({
    path: `${prefix}.hazmatInfo.${field}`,
    message: required,
  }));

const getHandlingUnitErrors = (hu: LtlHandlingUnit, huIndex: number): WizardFieldError[] => {
  const huPrefix = `handlingUnits.${huIndex}`;
  const dimensionErrors: WizardFieldError[] = [
    ...requirePositive(`${huPrefix}.length`, hu.length),
    ...requirePositive(`${huPrefix}.width`, hu.width),
    ...requirePositive(`${huPrefix}.height`, hu.height),
  ];
  if (hu.commodities.length === 0) {
    return [...dimensionErrors, { path: `${huPrefix}.commodities`, message: required }];
  }
  return [
    ...dimensionErrors,
    ...hu.commodities.flatMap((commodity, commIndex) => {
      const commPrefix = `${huPrefix}.commodities.${commIndex}`;
      return [
        ...requirePositive(`${commPrefix}.weight`, commodity.weight),
        ...requireText(`${commPrefix}.freightClass`, commodity.freightClass),
        ...getCommodityHazmatErrors(commodity, commPrefix),
      ];
    }),
  ];
};

const getHandlingUnitsErrors = (state: FreightBookingFormState): WizardFieldError[] =>
  state.handlingUnits.length === 0 ? [{ path: 'handlingUnits', message: required }] : state.handlingUnits.flatMap(getHandlingUnitErrors);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Errors that block fetching rates — a strict subset of getAllErrors.
 * Intentionally does not require contact fields (name, company, etc.) because
 * the rating request only needs ship-from, shipmentDate, destination geography,
 * and handling-unit data. Gating rates on the full booking-complete set would
 * leave the rates panel empty while the user is still filling in contact details.
 */
export const getQuoteBlockingErrors = (state: FreightBookingFormState): WizardFieldError[] => [
  ...requireText('selectedShipFromId', state.selectedShipFromId),
  ...requireShipDate(state.shipmentDate),
  ...getAddressErrors(state, QUOTE_REQUIRED_ADDRESS_FIELDS),
  ...getHandlingUnitsErrors(state),
];

/** True when the form has enough data to fetch carrier rates. */
export const isReadyToQuote = (state: FreightBookingFormState): boolean => getQuoteBlockingErrors(state).length === 0;

/** Returns all field-level errors for the given step. */
export const getStepErrors = (step: WizardStepId, formState: FreightBookingFormState): WizardFieldError[] => {
  switch (step) {
    case 'ship-from':
      return getShipFromErrors(formState);
    case 'ship-to':
      return getShipToErrors(formState);
    case 'handling-units':
      return getHandlingUnitsErrors(formState);
    case 'additional-details':
      return [];
  }
};

/**
 * Given a `fieldErrors` map (path → message) and a path segment prefix,
 * returns a new map containing only entries whose key starts with `prefix.`,
 * with the `prefix.` stripped from each key.
 *
 * The trailing dot is appended internally so callers pass a plain segment
 * (`'handlingUnits.0'`) and can't accidentally omit or double-add the separator.
 *
 * Example: sliceFieldErrors({ 'handlingUnits.0.length': 'Required' }, 'handlingUnits.0')
 *   → { 'length': 'Required' }
 */
export const sliceFieldErrors = (fieldErrors: Record<string, string>, prefix: string): Record<string, string> => {
  const dotPrefix = `${prefix}.`;
  return Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([path]) => path.startsWith(dotPrefix))
      .map(([path, msg]) => [path.slice(dotPrefix.length), msg]),
  );
};

export const WIZARD_STEP_IDS = ['ship-from', 'ship-to', 'handling-units', 'additional-details'] as const satisfies readonly WizardStepId[];

/** Returns all field-level errors across every step. */
export const getAllErrors = (formState: FreightBookingFormState): WizardFieldError[] =>
  WIZARD_STEP_IDS.flatMap((step) => getStepErrors(step, formState));

/** True when no step has any required-field violations. */
export const isFormComplete = (formState: FreightBookingFormState): boolean => getAllErrors(formState).length === 0;

/**
 * True when the given step has at least one required-field violation.
 * Signature is unchanged from the original so call sites in `FreightBookingWizard`
 * need no changes in Phase 1.
 */
export const hasStepErrors = (step: WizardStepId, formState: FreightBookingFormState): boolean => getStepErrors(step, formState).length > 0;

/**
 * Path used for the rate-missing error so consumers can discriminate it from
 * field-level gaps without parsing message text.
 */
export const SELECTED_RATE_PATH = 'selectedRate';

export type BookValidationContext = {
  selectedRate: { productTransactionId?: string } | null;
  /** False when the last re-quote returned Expired / NoMatch / failed; omit or true when available. */
  isRateAvailable?: boolean;
};

/** Superset of getAllErrors — additionally requires a selected rate that is still available. */
export const getBookBlockingErrors = (formState: FreightBookingFormState, context: BookValidationContext): WizardFieldError[] => [
  ...getAllErrors(formState),
  ...(context.selectedRate && context.isRateAvailable !== false ? [] : [{ path: SELECTED_RATE_PATH, message: required }]),
];

export const isReadyToBook = (formState: FreightBookingFormState, context: BookValidationContext): boolean =>
  getBookBlockingErrors(formState, context).length === 0;
